/**
 * MİDAS HESAP EKSTRESİ (PDF) — kullanıcı kararları 21–22.10.2026.
 * Biçim ve kararlar: `claude/notlar/2026-10-22-midas-ekstre-bicimi.md`.
 *
 * PDF cihazda okunur (`pdfMetin.ts`); burada yalnızca konumlu metin
 * parçalarından satırlar çıkar. Satırlar ŞABLON SÜTUNLARINA çevrilip GENEL
 * okuyucudan (`dosyayiOku`) geçer: kurallar tek kopya (Parafon öncesi
 * dökümlerdeki gibi). Kişisel bilgi (ad, kimlik no, adres, IBAN) hiçbir
 * satıra yazılmaz; açıklama sütunu yalnızca tanınan kalıplar için okunur.
 *
 * Bölümler: YATIRIM İŞLEMLERİ (alış/satış), HESAP İŞLEMLERİ (para yatırma,
 * döviz alış/satış, diğer gelir), TEMETTÜ İŞLEMLERİ, PORTFÖY ÖZETİ (hareket
 * değil — içe aktarmadan sonra adetlerin karşılaştırılması için).
 *
 * PİYASA: TL satırları "TR" — ekstre hisse ile fonu ayırmaz; katalogda BIST
 * hissesi ya da TEFAS fonu olarak aranır, ikisinde de varsa kullanıcı seçer
 * (kullanıcı, 25.10.2026: PPZ bir TEFAS fonu). Dolar satırları "ABD".
 *
 * Sütunlar başlığın yatay konumundan bulunur (her hücre PDF'te ayrı bir
 * parça); bir hücre en yakın başlığa düşer. Başlığın sırası ve açık adları
 * denetlenir: biçim değişmişse okunmaz, sebebi söylenir — tahmin yok.
 *
 * DÖVİZ ALIŞ / SATIŞ: TL ve yabancı para ekstreleri ayrıdır; bir işlemin iki
 * yarısı iki ekstrede, saniyesine kadar aynı zamanla (kullanıcı, 22.10.2026).
 * Eşi bulunmayan döviz satırı yazılmaz; "öbür ekstreyi de seçin" der.
 */

import type { Decimal, IsoDate, TransactionType } from "@parafon/contracts";
import { POSITION_SIGN } from "@parafon/contracts/local-api";
import type { EkstreSayfasi, MetinParcasi } from "./ekstreMaske";
import { M } from "../dil";
import { csvYaz, dosyayiOku, sade, SUTUNLAR, type Okuma, type OkunanSatir, type Sorun } from "./iceAktarma";

export const MIDAS_AD = M.iceAktar.midas.ad;
/** Parafon'daki tek hesap (kullanıcı kararı 22.10.2026: TL ve döviz aynı hesapta). */
export const MIDAS_HESAP = "Midas";

export interface MidasBelgesi {
  readonly ad: string;
  readonly sayfalar: readonly EkstreSayfasi[];
}

export interface OzetSatiri {
  readonly sembol: string;
  readonly piyasa: string;
  readonly paraBirimi: string;
  readonly adet: Decimal;
}

export interface MidasOkuma extends Okuma {
  readonly kaynakBicimi: string;
  /** Okunan belgelerin para birimleri (ör. ["TRY", "USD"]). */
  readonly belgeler: readonly { readonly ad: string; readonly paraBirimi: string; readonly donem: string }[];
  /** Hareket sayılmayan emirler (iptal, süresi dolan …) ve gerçekleşmeyen hesap işlemleri. */
  readonly atlanan: number;
  /** En son tarihli portföy özeti (karşılaştırma için); yoksa null. */
  readonly ozet: { readonly tarih: IsoDate; readonly satirlar: readonly OzetSatiri[] } | null;
}

/** Okunamayan satır; `kaynak` ekstredeki asıl satır (döviz eşleşmesinde yok). */
interface MidasSorunu {
  satir: SablonSatiri;
  mesaj: string;
  kaynak?: { bolum: string; hucreler: (readonly [string, string])[] };
}

export type MidasSonucu = { readonly tur: "tamam"; readonly okuma: MidasOkuma } | { readonly tur: "tanınmadı"; readonly sebep: string };

// ---------------------------------------------------------------------------
// Satırlar ve sütunlar
// ---------------------------------------------------------------------------

export type Satir = readonly MetinParcasi[];

/** Aynı dikey konumdaki parçalar bir satır (yazı boyunun yarısı payla). */
export function satirlar(parcalar: readonly MetinParcasi[]): Satir[] {
  const sirali = [...parcalar].sort((a, b) => a.y - b.y || a.x - b.x);
  const out: MetinParcasi[][] = [];
  for (const p of sirali) {
    const son = out[out.length - 1];
    const ilk = son?.[0];
    if (son !== undefined && ilk !== undefined && Math.abs(ilk.y - p.y) <= Math.max(ilk.boy, p.boy, 1) * 0.5) son.push(p);
    else out.push([p]);
  }
  return out.map((s) => s.sort((a, b) => a.x - b.x));
}

const merkez = (p: MetinParcasi): number => p.x + p.genislik / 2;
const metin = (s: Satir): string => s.map((p) => p.metin).join(" ");

type Bolum = "ozet" | "yatirim" | "hesap" | "temettu" | "transfer";

const BOLUM_BASLIKLARI: readonly (readonly [Bolum, string])[] = [
  ["ozet", "portfoy ozeti"],
  ["yatirim", "yatirim islemleri"],
  ["hesap", "hesap islemleri"],
  ["temettu", "temettu islemleri"],
  ["transfer", "hisse"],
];

function bolumBasligi(s: Satir): Bolum | null {
  const ilk = s[0];
  if (ilk === undefined || !ilk.metin.includes("(")) return null;
  const ad = sade(ilk.metin.split("(")[0] ?? "");
  for (const [b, baslik] of BOLUM_BASLIKLARI) if (ad === baslik || (b === "transfer" && ad.startsWith(baslik))) return b;
  return null;
}

/**
 * Beklenen başlıklar, SIRASIYLA. `null` = adı kurumda değişebilen (ya da
 * maskeli örnekte görülmeyen) sütun: yalnızca yeri denetlenir, `onEk`/`sonEk`
 * verilmişse o da.
 */
type Beklenen = { readonly ad: string } | { readonly onEk?: string; readonly sonEk?: string };

const BASLIKLAR: Readonly<Record<Exclude<Bolum, "transfer">, readonly (readonly [string, Beklenen])[]>> = {
  yatirim: [
    ["tarih", { ad: "tarih" }],
    ["emirTuru", { ad: "islem turu" }],
    ["sembol", { ad: "sembol" }],
    ["yon", { ad: "islem tipi" }],
    ["durum", { onEk: "islem" }],
    ["paraBirimi", { ad: "para birimi" }],
    ["emirAdedi", { ad: "emir adedi" }],
    ["emirTutari", { ad: "emir tutari" }],
    ["adet", { ad: "gerceklesen adet" }],
    ["fiyat", { ad: "ortalama islem fiyati" }],
    ["ucret", { ad: "islem ucreti" }],
    ["tutar", { ad: "islem tutari" }],
  ],
  hesap: [
    ["talep", { sonEk: "tarihi" }],
    ["tarih", { ad: "islem tarihi" }],
    ["tip", { ad: "islem tipi" }],
    ["aciklama", { ad: "islem aciklamasi" }],
    ["durum", { onEk: "islem" }],
    ["tutar", { onEk: "tutar" }],
  ],
  temettu: [
    ["tarih", { sonEk: "tarihi" }],
    ["arac", { ad: "sermaye piyasasi araci" }],
    ["brut", { ad: "brut temettu tutari" }],
    ["stopaj", { ad: "stopaj" }],
    ["net", { ad: "net temettu tutari" }],
  ],
  ozet: [
    ["arac", { ad: "sermaye piyasasi araci" }],
    ["adet", { ad: "adet" }],
    ["maliyet", { ad: "hisse basi ortalama maliyet" }],
    ["karZarar", { ad: "kar zarar" }],
    ["deger", { onEk: "toplam degeri" }],
  ],
};

interface Sutunlar {
  readonly anahtarlar: readonly string[];
  readonly merkezler: readonly number[];
}

function basligiOku(bolum: Exclude<Bolum, "transfer">, s: Satir): Sutunlar | string {
  const beklenen = BASLIKLAR[bolum];
  if (s.length !== beklenen.length) {
    return M.iceAktar.midas.sutunSayisi(s.length, beklenen.length, s.map((p) => p.metin).join(" | "));
  }
  for (let i = 0; i < beklenen.length; i += 1) {
    const ad = sade((s[i] as MetinParcasi).metin);
    const b = (beklenen[i] as readonly [string, Beklenen])[1];
    const tutar = "ad" in b ? ad === b.ad : (b.onEk === undefined || ad.startsWith(b.onEk)) && (b.sonEk === undefined || ad.endsWith(b.sonEk));
    if (!tutar) return M.iceAktar.midas.sutunBeklenmedik(i + 1, (s[i] as MetinParcasi).metin);
  }
  return { anahtarlar: beklenen.map(([a]) => a), merkezler: s.map(merkez) };
}

/** Her hücre en yakın başlık merkezine; aynı sütuna iki parça düşerse birleşir. */
function hucreler(s: Satir, sutun: Sutunlar): Record<string, string> {
  const out: Record<string, string> = {};
  for (const p of s) {
    let en = 0;
    let fark = Number.POSITIVE_INFINITY;
    sutun.merkezler.forEach((m, i) => {
      const f = Math.abs(m - merkez(p));
      if (f < fark) {
        fark = f;
        en = i;
      }
    });
    const k = sutun.anahtarlar[en] as string;
    out[k] = out[k] === undefined ? p.metin.trim() : `${out[k]} ${p.metin.trim()}`;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Değerler
// ---------------------------------------------------------------------------

/** "21/10/26 15:31:02" → { tarih: 2026-10-21, gosterim: 21.10.2026, saat }. */
function zamanOku(ham: string | undefined): { tarih: IsoDate; gosterim: string; saat: string | null } | null {
  const m = /^(\d{2})\/(\d{2})\/(\d{2})(?:\s+(\d{2}:\d{2}:\d{2}))?$/.exec((ham ?? "").trim());
  if (m === null) return null;
  const [, g, a, y, saat] = m;
  return { tarih: `20${y}-${a}-${g}`, gosterim: `${g}.${a}.20${y}`, saat: saat ?? null };
}

/** Tablo sayısı: virgül ondalık, binlik nokta isteğe bağlı; sondaki para kodu ayrılır. */
function tutarOku(ham: string | undefined): { sayi: string; para: string | null } | null {
  const m = /^(-?\d{1,3}(?:\.\d{3})+(?:,\d+)?|-?\d+(?:,\d+)?)(?:\s+([A-Z]{3}))?$/.exec((ham ?? "").trim());
  if (m === null) return null;
  return { sayi: m[1] as string, para: m[2] ?? null };
}

/** "1.234,56" → 1234.56 ölçekli tamsayı (`hane` ondalık). */
function olcekli(sayiTr: string, hane: number): bigint | null {
  const m = /^(-?)(\d+)(?:,(\d+))?$/.exec(sayiTr.replace(/\./g, ""));
  if (m === null) return null;
  const kesir = m[3] ?? "";
  if (kesir.length > hane) return null;
  const v = BigInt(`${m[2]}${kesir.padEnd(hane, "0")}`);
  return m[1] === "-" ? -v : v;
}

function olcektenTr(v: bigint, hane: number): string {
  const neg = v < 0n;
  const s = (neg ? -v : v).toString().padStart(hane + 1, "0");
  const tam = s.slice(0, s.length - hane);
  const kesir = s.slice(s.length - hane).replace(/0+$/, "");
  return `${neg ? "-" : ""}${tam}${kesir === "" ? "" : `,${kesir}`}`;
}

/** a ÷ b, `hane` ondalığa yarım yukarı (ikisi de pozitif). */
function bol(aTr: string, bTr: string, hane: number): string | null {
  const H = 12;
  const a = olcekli(aTr, H);
  const b = olcekli(bTr, H);
  if (a === null || b === null || b <= 0n || a < 0n) return null;
  const olcek = 10n ** BigInt(hane);
  const q = (a * olcek * 2n + b) / (b * 2n);
  return olcektenTr(q, hane);
}

// ---------------------------------------------------------------------------
// Okuma
// ---------------------------------------------------------------------------

const SABLON_BASLIK = SUTUNLAR.map((c) => c.baslik);
type SablonSatiri = Partial<Record<(typeof SUTUNLAR)[number]["anahtar"], string>>;

function sablon(s: SablonSatiri): string[] {
  return SUTUNLAR.map((c) => s[c.anahtar] ?? "");
}

interface DovizYarisi {
  readonly zaman: string;
  readonly gosterim: string;
  readonly tip: "alis" | "satis";
  readonly paraBirimi: string;
  readonly tutar: string;
  readonly kur: string | null;
  readonly belge: string;
}

function belgeyiOku(b: MidasBelgesi): {
  satirlar: SablonSatiri[];
  sorunlar: MidasSorunu[];
  doviz: DovizYarisi[];
  atlanan: number;
  ozet: { tarih: IsoDate; satirlar: OzetSatiri[] } | null;
  paraBirimi: string | null;
  donem: string;
  hata: string | null;
} {
  const out: SablonSatiri[] = [];
  const sorunlar: MidasSorunu[] = [];
  const doviz: DovizYarisi[] = [];
  let atlanan = 0;
  let ozet: { tarih: IsoDate; satirlar: OzetSatiri[] } | null = null;
  const paralar = new Set<string>();
  let donem = "";
  let bolum: Bolum | null = null;
  let sutun: Sutunlar | null = null;
  let ozetTarihi: IsoDate | null = null;

  for (const sayfa of b.sayfalar) {
    for (const s of satirlar(sayfa.parcalar)) {
      const ilk = s[0];
      if (ilk === undefined) continue;
      // Sayfa üstü (kurum bilgisi) ve altı (sayfa numarası) atlanır.
      if (ilk.y < 70 || ilk.y > sayfa.yukseklik - 20) continue;
      const tumu = metin(s);
      if (donem === "" && /HESAP EKSTRES/i.test(tumu)) {
        const m = /(\d{2}\/\d{2}\/\d{2})\s*-\s*(\d{2}\/\d{2}\/\d{2})/.exec(tumu.replace(/\s+/g, " "));
        if (m !== null) donem = `${zamanOku(m[1])?.gosterim ?? ""} – ${zamanOku(m[2])?.gosterim ?? ""}`;
      }
      const yeni = bolumBasligi(s);
      if (yeni !== null) {
        bolum = yeni;
        sutun = null;
        if (yeni === "ozet") {
          const m = /\((?:\s*)(\d{2}\/\d{2}\/\d{2})\s*\)/.exec(tumu);
          ozetTarihi = zamanOku(m?.[1])?.tarih ?? null;
        }
        continue;
      }
      // Dipnot ("*Kar zarar …", "* Şirketimiz …") bölümü kapatır.
      if (ilk.metin.trim().startsWith("*")) {
        bolum = null;
        continue;
      }
      if (bolum === null) continue;
      if (bolum === "transfer") {
        // Hisse transferi henüz okunmuyor: boş değilse tahmin yok, belge okunmaz.
        if (!/kayit bulunmamaktadir/.test(sade(tumu))) {
          return { satirlar: [], sorunlar: [], doviz: [], atlanan: 0, ozet: null, paraBirimi: null, donem, hata: M.iceAktar.midas.transferVar };
        }
        continue;
      }
      if (sutun === null) {
        if (s.length === 1 && /kayit bulunmamaktadir/.test(sade(tumu))) continue;
        const r = basligiOku(bolum, s);
        if (typeof r === "string") {
          return { satirlar: [], sorunlar: [], doviz: [], atlanan: 0, ozet: null, paraBirimi: null, donem, hata: M.iceAktar.midas.baslikTaninmadi(b.ad, bolum, r) };
        }
        sutun = r;
        continue;
      }
      // Sayfa başında tekrarlanan başlık.
      if (typeof basligiOku(bolum, s) !== "string") continue;
      const h = hucreler(s, sutun);
      const sorun = (satir: SablonSatiri, mesaj: string): void => {
        sorunlar.push({ satir, mesaj, kaynak: { bolum: bolum as string, hucreler: Object.entries(h) } });
      };

      if (bolum === "ozet") {
        const sembol = (h["arac"] ?? "").split(" - ")[0]?.trim() ?? "";
        const adet = tutarOku(h["adet"]);
        const para = tutarOku(h["deger"])?.para ?? null;
        if (ozetTarihi !== null && sembol !== "" && adet !== null && para !== null) {
          ozet ??= { tarih: ozetTarihi, satirlar: [] };
          ozet.satirlar.push({ sembol, piyasa: para === "TRY" ? "TR" : "ABD", paraBirimi: para, adet: adet.sayi });
        }
        continue;
      }

      if (bolum === "yatirim") {
        const z = zamanOku(h["tarih"]);
        const pb = (h["paraBirimi"] ?? "").trim();
        const sembol = (h["sembol"] ?? "").trim();
        const yon = sade(h["yon"] ?? "");
        const durum = sade(h["durum"] ?? "");
        const fiyatHam = (h["fiyat"] ?? "").trim();
        const taslak: SablonSatiri = {
          tarih: z?.gosterim ?? h["tarih"] ?? "",
          hesap: MIDAS_HESAP,
          varlik: sembol,
          piyasa: pb === "TRY" ? "TR" : "ABD",
          paraBirimi: pb,
          not: z?.saat === null || z === null ? "" : `Midas · ${z.saat}`,
        };
        // KISMİ GERÇEKLEŞME (kullanıcı, 25.10.2026): "Kalan İptal Edildi" = emrin
        // bir kısmı gerçekleşti, kalanı iptal edildi. Gerçekleşen kısım hareket.
        const kismi = durum === "kalan iptal edildi";
        if (durum !== "gerceklesti" && !kismi) {
          // Gerçekleşmeyen emir hareket değildir; fiyatı varsa durum tanınmıyor demektir.
          if (fiyatHam === "-" || fiyatHam === "") atlanan += 1;
          else sorun(taslak, M.iceAktar.midas.emirDurumu(h["durum"] ?? ""));
          continue;
        }
        paralar.add(pb);
        const adet = tutarOku(h["adet"]);
        const fiyat = tutarOku(fiyatHam);
        const tutar = tutarOku(h["tutar"]);
        const ucret = (h["ucret"] ?? "").trim();
        const tur = yon === "alis" ? "Alış" : yon === "satis" ? "Satış" : null;
        if (z === null || tur === null || adet === null || fiyat === null || tutar === null) {
          sorun(taslak, M.iceAktar.midas.satirOkunamadi);
          continue;
        }
        // İşlem tutarı = adet × ortalama fiyat (ücret hariç; kullanıcı 22.10.2026).
        // Ortalama fiyat yuvarlanmış yazılır: fark adet × yarım kuruşu aşmamalı.
        const a = olcekli(adet.sayi, 12);
        const f = olcekli(fiyat.sayi, 12);
        const t = olcekli(tutar.sayi, 12);
        if (a === null || f === null || t === null || a <= 0n) {
          sorun(taslak, M.iceAktar.midas.adetFiyatTutar);
          continue;
        }
        const carpim = (a * f) / 10n ** 12n;
        const fark = carpim > t ? carpim - t : t - carpim;
        const pay = (a * 5n) / 1000n + 10n ** 10n;
        if (fark > pay) {
          sorun(taslak, M.iceAktar.midas.tutarTutmuyor(olcektenTr(carpim, 12), tutar.sayi));
          continue;
        }
        const birim = bol(tutar.sayi, adet.sayi, 12);
        if (birim === null) {
          sorun(taslak, M.iceAktar.midas.birimFiyat);
          continue;
        }
        out.push({
          ...taslak,
          tur,
          miktar: adet.sayi,
          fiyat: birim,
          masraf: ucret === "-" ? "" : (tutarOku(ucret)?.sayi ?? ucret),
          not: [
            taslak.not,
            kismi
              ? (h["emirAdedi"] ?? "-") === "-"
                ? M.iceAktar.not.kismenTutar(h["emirTutari"] ?? "?", adet.sayi)
                : M.iceAktar.not.kismenAdet(h["emirAdedi"] ?? "?", adet.sayi)
              : null,
            M.iceAktar.not.birimFiyat(fiyat.sayi),
          ]
            .filter((x) => x !== null && x !== "")
            .join(" · "),
          kaynakKimligi: `midas:Y:${z.tarih}T${z.saat ?? ""}:${sembol}:${yon}:${adet.sayi}:${tutar.sayi}`,
        });
        continue;
      }

      if (bolum === "hesap") {
        const z = zamanOku(h["tarih"]);
        const tip = sade(h["tip"] ?? "");
        const durum = sade(h["durum"] ?? "");
        const tutar = tutarOku(h["tutar"]);
        const taslak: SablonSatiri = { tarih: z?.gosterim ?? h["tarih"] ?? "", hesap: MIDAS_HESAP, paraBirimi: tutar?.para ?? "" };
        if (durum !== "gerceklesti") {
          atlanan += 1;
          continue;
        }
        if (z === null || tutar === null || tutar.para === null) {
          sorun(taslak, M.iceAktar.midas.hesapIslemiOkunamadi);
          continue;
        }
        paralar.add(tutar.para);
        const kimlik = `midas:H:${z.tarih}T${z.saat ?? ""}:${tip}:${tutar.sayi}:${tutar.para}`;
        const not = z.saat === null ? "Midas" : `Midas · ${z.saat}`;
        if (tip === "para yatirma" || tip === "para cekme") {
          out.push({ ...taslak, tur: tip === "para yatirma" ? "Para yatırma" : "Para çekme", tutar: tutar.sayi, not, kaynakKimligi: kimlik });
        } else if (tip === "diger gelir" && /\bnema\b/.test(sade(h["aciklama"] ?? ""))) {
          // Nema geliri = nakdin faizi (kullanıcı, 22.10.2026).
          out.push({ ...taslak, tur: "Faiz", tutar: tutar.sayi, not: `${not} · ${M.iceAktar.not.nemaGeliri}`, kaynakKimligi: kimlik });
        } else if (tip === "diger gider" && /\bstopaj\b/.test(sade(h["aciklama"] ?? ""))) {
          // Hesaptan kesilen stopaj = nakitten vergi ödemesi, ödendiği yıla
          // (kullanıcı, 25.10.2026). Ekstre gideri eksi yazar; ödeme artı tutulur.
          out.push({ ...taslak, tur: "Vergi ödemesi", tutar: tutar.sayi.replace(/^-/, ""), not: `${not} · ${M.iceAktar.not.stopaj}`, kaynakKimligi: kimlik });
        } else if (tip === "doviz alis" || tip === "doviz satis") {
          const kur = /^(\d+(?:,\d+)?)\s+[A-Z]{3}\/[A-Z]{3}\b/.exec((h["aciklama"] ?? "").trim())?.[1] ?? null;
          doviz.push({
            zaman: `${z.tarih}T${z.saat ?? ""}`,
            gosterim: z.gosterim,
            tip: tip === "doviz alis" ? "alis" : "satis",
            paraBirimi: tutar.para,
            tutar: tutar.sayi,
            kur,
            belge: b.ad,
          });
        } else {
          sorun(taslak, M.iceAktar.midas.hesapIslemiTaninmadi(h["tip"] ?? "", tip === "diger gelir" ? (h["aciklama"] ?? "") : null));
        }
        continue;
      }

      // bolum === "temettu"
      const z = zamanOku(h["tarih"]);
      const sembol = (h["arac"] ?? "").split(" - ")[0]?.trim() ?? "";
      const brut = tutarOku(h["brut"]);
      const stopaj = tutarOku(h["stopaj"]);
      const net = tutarOku(h["net"]);
      const pb = brut?.para ?? "";
      const taslak: SablonSatiri = {
        tarih: z?.gosterim ?? h["tarih"] ?? "",
        hesap: MIDAS_HESAP,
        varlik: sembol,
        piyasa: pb === "TRY" ? "TR" : "ABD",
        paraBirimi: pb,
      };
      if (z === null || sembol === "" || brut === null || stopaj === null || net === null) {
        sorun(taslak, M.iceAktar.midas.temettuOkunamadi);
        continue;
      }
      const b12 = olcekli(brut.sayi, 12);
      const s12 = olcekli(stopaj.sayi, 12);
      const n12 = olcekli(net.sayi, 12);
      if (b12 === null || s12 === null || n12 === null || b12 - s12 !== n12) {
        sorun(taslak, M.iceAktar.midas.temettuTutmuyor(brut.sayi, stopaj.sayi, net.sayi));
        continue;
      }
      paralar.add(pb);
      out.push({
        ...taslak,
        tur: "Temettü",
        tutar: brut.sayi,
        vergi: stopaj.sayi,
        valor: z.gosterim,
        not: `Midas · ${M.iceAktar.not.odemeTarihi}`,
        kaynakKimligi: `midas:T:${z.tarih}:${sembol}:${brut.sayi}`,
      });
    }
  }
  const pb = paralar.size === 1 ? ([...paralar][0] as string) : null;
  return { satirlar: out, sorunlar, doviz, atlanan, ozet, paraBirimi: pb, donem, hata: null };
}

/** PDF'ler Midas ekstresi mi: kurum adı + başlık + bölümler. */
function midasMi(b: MidasBelgesi): boolean {
  const hepsi = b.sayfalar.flatMap((s) => s.parcalar.map((p) => p.metin)).join(" ");
  return /midas/i.test(hepsi) && /HESAP EKSTRES/i.test(hepsi) && /YATIRIM İŞLEMLERİ|YATIRIM ISLEMLERI/i.test(hepsi);
}

export function midasOku(belgeler: readonly MidasBelgesi[]): MidasSonucu {
  if (belgeler.length === 0) return { tur: "tanınmadı", sebep: M.iceAktar.midas.dosyaYok };
  const taninmayan = belgeler.filter((b) => !midasMi(b));
  if (taninmayan.length > 0) {
    return { tur: "tanınmadı", sebep: M.iceAktar.midas.taninmayan(taninmayan.map((b) => b.ad).join(", ")) };
  }
  const satirlar: SablonSatiri[] = [];
  const sorunlar: MidasSorunu[] = [];
  const doviz: DovizYarisi[] = [];
  const bilgi: { ad: string; paraBirimi: string; donem: string }[] = [];
  let atlanan = 0;
  let ozet = null as MidasOkuma["ozet"];
  for (const b of belgeler) {
    const r = belgeyiOku(b);
    if (r.hata !== null) return { tur: "tanınmadı", sebep: r.hata };
    satirlar.push(...r.satirlar);
    sorunlar.push(...r.sorunlar);
    doviz.push(...r.doviz);
    atlanan += r.atlanan;
    bilgi.push({ ad: b.ad, paraBirimi: r.paraBirimi ?? "—", donem: r.donem });
    if (r.ozet !== null && r.ozet.satirlar.length > 0) {
      ozet =
        ozet === null || r.ozet.tarih > ozet.tarih
          ? r.ozet
          : ozet.tarih === r.ozet.tarih
            ? { tarih: ozet.tarih, satirlar: [...ozet.satirlar, ...r.ozet.satirlar] }
            : ozet;
    }
  }

  // DÖVİZ: iki yarı aynı zamanla; biri TL, öbürü döviz.
  const gruplar = new Map<string, DovizYarisi[]>();
  for (const d of doviz) {
    const k = `${d.zaman}|${d.tip}`;
    gruplar.set(k, [...(gruplar.get(k) ?? []), d]);
  }
  for (const [, g] of gruplar) {
    const tl = g.filter((d) => d.paraBirimi === "TRY");
    const yp = g.filter((d) => d.paraBirimi !== "TRY");
    const ilk = g[0] as DovizYarisi;
    const taslak: SablonSatiri = { tarih: ilk.gosterim, hesap: MIDAS_HESAP, tur: "Döviz alış/satış", paraBirimi: ilk.paraBirimi, tutar: ilk.tutar };
    if (tl.length !== 1 || yp.length !== 1) {
      sorunlar.push({
        satir: taslak,
        mesaj:
          g.length === 1
            ? M.iceAktar.midas.dovizYarisiYok(ilk.tip === "alis", ilk.belge)
            : M.iceAktar.midas.dovizCoklu,
      });
      continue;
    }
    const t = tl[0] as DovizYarisi;
    const y = yp[0] as DovizYarisi;
    if (t.kur !== null && y.kur !== null && t.kur !== y.kur) {
      sorunlar.push({ satir: taslak, mesaj: M.iceAktar.midas.kurFarkli(t.kur, y.kur) });
      continue;
    }
    const [verilen, alinan] = ilk.tip === "alis" ? [t, y] : [y, t];
    satirlar.push({
      tarih: ilk.gosterim,
      hesap: MIDAS_HESAP,
      tur: ilk.tip === "alis" ? "Döviz alış" : "Döviz satış",
      paraBirimi: verilen.paraBirimi,
      tutar: verilen.tutar,
      alinanTutar: alinan.tutar,
      alinanParaBirimi: alinan.paraBirimi,
      not: `Midas · ${ilk.zaman.slice(11)}${t.kur === null ? "" : ` · ${M.iceAktar.not.kur(t.kur)}`}`,
      kaynakKimligi: `midas:D:${ilk.zaman}:${ilk.tip}`,
    });
  }

  // Tarih sırası: kimlikler ve önizleme kararlı.
  satirlar.sort((a, b) => ((a.tarih ?? "").split(".").reverse().join("") < (b.tarih ?? "").split(".").reverse().join("") ? -1 : 1));
  const o = dosyayiOku(csvYaz([SABLON_BASLIK, ...satirlar.map(sablon)], ";"), "tr");
  const ekSorunlar: Sorun[] = sorunlar.map((x, i) => ({
    satirNo: satirlar.length + 2 + i,
    ham: sablon(x.satir),
    mesajlar: [x.mesaj],
    ...(x.kaynak === undefined ? {} : { kaynak: x.kaynak }),
  }));
  const okuma: MidasOkuma = {
    ...o,
    sorunlar: [...o.sorunlar, ...ekSorunlar],
    kaynakBicimi: MIDAS_AD,
    belgeler: bilgi,
    atlanan,
    ozet,
  };
  return { tur: "tamam", okuma };
}

// ---------------------------------------------------------------------------
// Portföy özetiyle karşılaştırma (içe aktarmadan sonra)
// ---------------------------------------------------------------------------

export interface OzetKarsilastirma {
  readonly sembol: string;
  readonly ekstrede: Decimal;
  /** Kasadaki adet (içe aktarılacaklar dahil); varlık eşleşmediyse null. */
  readonly kasada: Decimal | null;
  readonly tutuyor: boolean;
}

const E9 = 9;

function e9(d: string): bigint | null {
  const m = /^(-?)(\d+)(?:[.,](\d+))?$/.exec(d.trim());
  if (m === null || (m[3] ?? "").length > E9) return null;
  const v = BigInt(`${m[2]}${(m[3] ?? "").padEnd(E9, "0")}`);
  return m[1] === "-" ? -v : v;
}

/**
 * Ekstrenin portföy özetindeki adetler, kasanın (aynı hesap, özet gününe
 * kadar) adetleriyle. `hareketler`: o hesabın kasadaki hareketleri ile
 * içe aktarılacak olanlar (kimliğe göre tekil); `varlikKimligi`: özet
 * satırının kasadaki varlık anahtarı (eşleşmediyse null).
 */
export function ozetKarsilastir(
  ozet: { readonly tarih: IsoDate; readonly satirlar: readonly OzetSatiri[] },
  hareketler: readonly { readonly type: TransactionType; readonly tradeDate: IsoDate; readonly quantity: Decimal; readonly varlik: string }[],
  varlikKimligi: (s: OzetSatiri) => string | null,
): readonly OzetKarsilastirma[] {
  const toplam = new Map<string, bigint>();
  for (const h of hareketler) {
    if (h.tradeDate > ozet.tarih) continue;
    const q = e9(h.quantity);
    if (q === null) continue;
    toplam.set(h.varlik, (toplam.get(h.varlik) ?? 0n) + BigInt(POSITION_SIGN[h.type]) * q);
  }
  return ozet.satirlar.map((s) => {
    const k = varlikKimligi(s);
    const beklenen = e9(s.adet.replace(/\./g, "").replace(",", "."));
    if (k === null || beklenen === null) return { sembol: s.sembol, ekstrede: s.adet, kasada: null, tutuyor: false };
    const v = toplam.get(k) ?? 0n;
    return { sembol: s.sembol, ekstrede: s.adet, kasada: olcektenTr(v, E9), tutuyor: v === beklenen };
  });
}

export type { OkunanSatir };
